import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, LogOut, ShieldCheck } from 'lucide-react';
import { ROTULO_PAPEL, type UsuarioDoEscritorio } from '@social/shared';
import {
  Botao,
  Campo,
  CONTROLE_BARRA,
  Entrada,
  Etiqueta,
  Selecao,
} from '../componentes/ui';
import { Modal } from '../componentes/modal';
import { Spinner } from '../componentes/carregando';
import { ErroApi } from '../lib/api';
import { apiOperador, useOperador } from '../lib/sessao-operador';
import { moeda } from './sociedade-comum';

/*
  A tela da OPERAÇÃO — todos os escritórios, e o que fazer com cada um.

  ## As seis perguntas (skill `social-desenho`)
  1. **Qual pergunta a pessoa traz?** "Este escritório está em dia — e se não
     está, o que eu faço agora?" Quem abre esta tela ou atende um chamado ou
     faz cobrança ativa; nos dois casos vem com um escritório em mente.
  2. **Quantos registros?** Dezenas a poucas centenas de escritórios. Busca
     sim; paginação não, porque a lista inteira cabe numa rolagem e o operador
     compara linhas entre si — paginar atrapalharia a comparação.
  3. **Resultado vs aparência?** A busca e o filtro de situação mudam o
     resultado, e ficam soltos na barra. Não há nada de aparência para colapsar.
  4. **Barra ou gaveta?** Só barra: são dois controles.
  5. **375px?** O operador trabalha em mesa, mas a tabela vira lista em telas
     estreitas de qualquer forma — atender chamado pelo celular acontece.
  6. **O que promete e não existe?** Cancelar assinatura não está aqui: a API
     tem o método, mas cancelar é irreversível e merece uma confirmação que
     esta tela ainda não tem. Botão sem o cuidado que a ação pede é pior que
     botão nenhum.
*/

interface Escritorio {
  id: string;
  nome: string;
  cnpj: string | null;
  emailCobranca: string | null;
  ativo: boolean;
  criadoEm: string;
  assinaturaId: string | null;
  situacao: string | null;
  origem: string | null;
  inicio: string | null;
  fim: string | null;
  precoContratadoCentavos: number | null;
  planoCodigo: string | null;
  planoNome: string | null;
  sociedades: number;
}

const ROTULO: Record<string, string> = {
  TRIAL: 'Em teste',
  ATIVA: 'Ativa',
  INADIMPLENTE: 'Em atraso',
  SUSPENSA: 'Suspensa',
  CANCELADA: 'Cancelada',
  EXPIRADA: 'Expirada',
};

function tom(situacao: string | null): 'neutro' | 'ok' | 'alerta' | 'erro' {
  if (situacao === 'ATIVA') return 'ok';
  if (situacao === 'TRIAL') return 'neutro';
  if (situacao === 'INADIMPLENTE') return 'alerta';
  return 'erro';
}

export function OperadorEscritorios(): JSX.Element {
  const fila = useQueryClient();
  const operador = useOperador((s) => s.operador);
  const sair = useOperador((s) => s.sair);
  const ehAdmin = operador?.nivel === 'ADMIN';

  const [busca, setBusca] = useState('');
  const [somentePendentes, setSomentePendentes] = useState(false);
  const [abertoId, setAbertoId] = useState<string | null>(null);

  const lista = useQuery({
    queryKey: ['operador', 'escritorios'] as const,
    queryFn: () => apiOperador.get<Escritorio[]>('/operador/escritorios'),
  });

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    /*
      UM filtro, não uma cadeia: cada `.filter()` extra percorre a lista de
      novo, e mais importante — condições espalhadas em vários passos ficam
      difíceis de ler juntas quando alguém precisa entender por que uma linha
      sumiu.
    */
    return (lista.data ?? []).filter((e) => {
      if (termo && !e.nome.toLowerCase().includes(termo) && !e.cnpj?.includes(termo)) {
        return false;
      }
      if (somentePendentes) {
        // "Pendente" para o operador é o que precisa de ação: em atraso,
        // suspenso, ou sem assinatura nenhuma.
        const precisa =
          e.situacao === 'INADIMPLENTE' || e.situacao === 'SUSPENSA' || !e.assinaturaId;
        if (!precisa) return false;
      }
      return true;
    });
  }, [lista.data, busca, somentePendentes]);

  return (
    <div className="bg-palco min-h-dvh">
      <header className="border-tinta-100 bg-superficie flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <ShieldCheck aria-hidden className="text-marca-600 size-5" />
          <span className="text-tinta-900 font-semibold">Operação Specializer</span>
          {operador && (
            <Etiqueta tom={ehAdmin ? 'ok' : 'neutro'}>{operador.nivel}</Etiqueta>
          )}
        </div>
        <button type="button" className={`${CONTROLE_BARRA} px-3`} onClick={sair}>
          <LogOut aria-hidden className="size-4" />
          Sair
        </button>
      </header>

      <main className="mx-auto flex max-w-6xl flex-col gap-4 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Campo rotulo="Buscar escritório" id="buscaEscritorio">
            <Entrada
              id="buscaEscritorio"
              value={busca}
              placeholder="Nome ou CNPJ"
              onChange={(e) => setBusca(e.target.value)}
            />
          </Campo>

          <label className="text-tinta-700 flex h-11 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={somentePendentes}
              onChange={(e) => setSomentePendentes(e.target.checked)}
            />
            Só quem precisa de ação
          </label>

          <span className="text-tinta-500 ml-auto text-sm tabular-nums">
            {filtrados.length} de {lista.data?.length ?? 0}
          </span>
        </div>

        {lista.isPending && (
          <p className="text-tinta-500 flex items-center gap-2 text-sm">
            <Spinner /> Carregando escritórios…
          </p>
        )}

        {lista.isError && (
          <p
            role="alert"
            className="bg-erro-100 text-erro-600 rounded-lg px-4 py-3 text-sm"
          >
            Não foi possível listar. A sessão pode ter expirado — entre de novo.
          </p>
        )}

        <ul className="flex flex-col gap-2">
          {filtrados.map((e) => (
            <li
              key={e.id}
              className="border-tinta-100 bg-superficie flex flex-col gap-3 rounded-xl border p-4"
            >
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="min-w-0 flex-1">
                  <p className="text-tinta-900 truncate font-medium">{e.nome}</p>
                  <p className="text-tinta-500 text-xs tabular-nums">
                    {e.cnpj ? formatarCnpj(e.cnpj) : 'sem CNPJ'} ·{' '}
                    {e.sociedades.toLocaleString('pt-BR')} sociedades
                  </p>
                </div>

                <Etiqueta tom={tom(e.situacao)}>
                  {e.situacao ? (ROTULO[e.situacao] ?? e.situacao) : 'Sem assinatura'}
                </Etiqueta>

                <div className="text-tinta-500 text-xs">
                  {e.planoNome ?? '—'}
                  {e.fim && <> · até {dataCurta(e.fim)}</>}
                </div>

                <span className="text-tinta-900 text-sm font-medium tabular-nums">
                  {e.precoContratadoCentavos === null
                    ? '—'
                    : e.precoContratadoCentavos === 0
                      ? 'Cortesia'
                      : moeda(e.precoContratadoCentavos)}
                </span>

                <Botao
                  variante="secundaria"
                  onClick={() => setAbertoId(abertoId === e.id ? null : e.id)}
                >
                  {abertoId === e.id ? 'Fechar' : 'Agir'}
                </Botao>
              </div>

              {abertoId === e.id && (
                <Acoes
                  escritorio={e}
                  podeAgir={ehAdmin}
                  aoConcluir={() =>
                    fila.invalidateQueries({ queryKey: ['operador', 'escritorios'] })
                  }
                />
              )}
            </li>
          ))}
        </ul>

        {!lista.isPending && filtrados.length === 0 && (
          <p className="text-tinta-500 text-sm">Nenhum escritório com esse filtro.</p>
        )}
      </main>
    </div>
  );
}

/**
 * As ações sobre um escritório.
 *
 * Cada uma exige MOTIVO, e o botão fica desabilitado sem ele. Não é
 * burocracia: o motivo entra na trilha de eventos junto com o operador, e é o
 * que responde "quem fez isso, e por quê" seis meses depois — quando ninguém
 * lembra do chamado que originou a ação.
 */
function Acoes({
  escritorio,
  podeAgir,
  aoConcluir,
}: {
  escritorio: Escritorio;
  podeAgir: boolean;
  aoConcluir: () => void;
}): JSX.Element {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [senha, setSenha] = useState(false);

  const agir = useMutation({
    mutationFn: ({ acao, corpo }: { acao: string; corpo: Record<string, unknown> }) =>
      apiOperador.post<unknown>(`/operador/escritorios/${escritorio.id}/${acao}`, corpo),
    onSuccess: () => {
      setMotivo('');
      setErro(null);
      aoConcluir();
    },
    onError: (e) => {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível concluir a ação.');
    },
  });

  const pronto = motivo.trim().length >= 3 && podeAgir && !agir.isPending;

  return (
    <div className="border-tinta-100 flex flex-col gap-3 border-t pt-3">
      {!podeAgir && (
        <p className="text-tinta-500 text-sm">
          Seu nível é SUPORTE: você enxerga o estado, mas não age sobre a assinatura.
        </p>
      )}

      {erro && (
        <p
          role="alert"
          className="bg-erro-100 text-erro-600 rounded-lg px-4 py-3 text-sm"
        >
          {erro}
        </p>
      )}

      <Campo
        rotulo="Motivo"
        id={`motivo-${escritorio.id}`}
        obrigatorio
        dica="Fica registrado na trilha do escritório, com o seu nome."
      >
        <Entrada
          id={`motivo-${escritorio.id}`}
          value={motivo}
          placeholder="PIX recebido em 31/08, comprovante no e-mail"
          onChange={(e) => setMotivo(e.target.value)}
        />
      </Campo>

      <div className="flex flex-wrap gap-2">
        <Botao
          variante="secundaria"
          disabled={!pronto}
          onClick={() =>
            agir.mutate({
              acao: 'contratar',
              corpo: {
                planoCodigo: 'ESSENCIAL_MENSAL',
                origem: 'CORTESIA',
                motivo,
              },
            })
          }
        >
          Conceder cortesia
        </Botao>

        {/*
          Destravar o escritório que perdeu a senha do administrador.

          Existe porque o autocadastro cria escritórios de UM administrador e o
          produto não envia e-mail: sem esta porta, "esqueci a senha" é o fim
          da linha para o cliente inteiro. É o poder mais perigoso da
          plataforma — daí o mesmo motivo obrigatório das outras ações, e o
          diálogo, que obriga a escolher a pessoa em vez de acertar de primeira.
        */}
        <Botao variante="secundaria" disabled={!podeAgir} onClick={() => setSenha(true)}>
          <KeyRound className="size-4" aria-hidden />
          Destravar acesso
        </Botao>

        {escritorio.situacao === 'SUSPENSA' ? (
          <Botao
            variante="secundaria"
            disabled={!pronto}
            onClick={() => agir.mutate({ acao: 'reativar', corpo: { motivo } })}
          >
            Reativar
          </Botao>
        ) : (
          <Botao
            variante="secundaria"
            disabled={!pronto || !escritorio.assinaturaId}
            title={
              escritorio.assinaturaId ? undefined : 'Este escritório não tem assinatura.'
            }
            onClick={() => agir.mutate({ acao: 'suspender', corpo: { motivo } })}
          >
            Suspender
          </Botao>
        )}
      </div>

      <DialogoSenhaDoOperador
        aberto={senha}
        escritorio={escritorio}
        motivo={motivo}
        aoFechar={() => setSenha(false)}
        aoConcluir={() => {
          setSenha(false);
          setMotivo('');
          aoConcluir();
        }}
      />
    </div>
  );
}

/**
 * O operador define a senha de alguém de OUTRA empresa.
 *
 * Pede a pessoa numa lista, e não o e-mail digitado: o operador está agindo
 * sobre uma casa que não é a dele, e escolher de uma lista é o que impede
 * destravar a conta errada por um caractere.
 */
function DialogoSenhaDoOperador({
  aberto,
  escritorio,
  motivo,
  aoFechar,
  aoConcluir,
}: {
  aberto: boolean;
  escritorio: Escritorio;
  motivo: string;
  aoFechar: () => void;
  aoConcluir: () => void;
}): JSX.Element {
  const [usuarioId, setUsuarioId] = useState('');
  const [senhaNova, setSenhaNova] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const detalhe = useQuery({
    queryKey: ['operador-escritorio', escritorio.id],
    queryFn: () =>
      apiOperador.get<{ usuarios: UsuarioDoEscritorio[] }>(
        `/operador/escritorios/${escritorio.id}`,
      ),
    enabled: aberto,
  });

  const definir = useMutation({
    mutationFn: () =>
      apiOperador.post<{ ok: true }>(`/operador/escritorios/${escritorio.id}/senha`, {
        usuarioId,
        senhaNova,
        motivo,
      }),
    onSuccess: () => {
      setSenhaNova('');
      setUsuarioId('');
      setErro(null);
      aoConcluir();
    },
    onError: (e) =>
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível definir a senha.'),
  });

  const usuarios = detalhe.data?.usuarios ?? [];
  const pronto = usuarioId !== '' && senhaNova.length >= 12 && motivo.trim().length >= 10;

  return (
    <Modal
      aberto={aberto}
      titulo={`Destravar acesso — ${escritorio.nome}`}
      descricao="A pessoa entra com a senha nova e as sessões abertas dela são encerradas."
      tamanho="pequeno"
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao variante="secundaria" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao disabled={!pronto || definir.isPending} onClick={() => definir.mutate()}>
            {definir.isPending ? 'Definindo…' : 'Definir senha'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {erro && (
          <p role="alert" className="text-erro-600 text-sm">
            {erro}
          </p>
        )}

        <Campo rotulo="Pessoa" id={`alvo-${escritorio.id}`} obrigatorio>
          <Selecao
            id={`alvo-${escritorio.id}`}
            value={usuarioId}
            onChange={(e) => setUsuarioId(e.target.value)}
          >
            <option value="">Selecione…</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome} — {ROTULO_PAPEL[u.papel]}
                {u.ativo ? '' : ' (desligado)'}
              </option>
            ))}
          </Selecao>
        </Campo>

        <Campo
          rotulo="Senha nova"
          id={`senha-${escritorio.id}`}
          obrigatorio
          dica="Mínimo de 12. Combine com a pessoa por um canal que você confie."
        >
          <Entrada
            id={`senha-${escritorio.id}`}
            type="password"
            autoComplete="new-password"
            minLength={12}
            value={senhaNova}
            onChange={(e) => setSenhaNova(e.target.value)}
          />
        </Campo>

        {/*
          O motivo é o mesmo campo da barra de ações, e não um segundo aqui:
          duas caixas de motivo na mesma tela ensinariam a preencher qualquer
          coisa numa delas.
        */}
        {motivo.trim().length < 10 && (
          <p className="text-alerta-600 text-sm">
            Escreva o motivo (10 caracteres ou mais) no campo acima, antes de definir a
            senha. Ele vai para o log da plataforma.
          </p>
        )}
      </div>
    </Modal>
  );
}

function formatarCnpj(d: string): string {
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

function dataCurta(iso: string): string {
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia ?? ''}/${mes ?? ''}/${ano ?? ''}`;
}
